import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell, BellOff, Check, ChevronDown, Trash2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { INVITABLE_ROLES, ROLE_LABELS, type Role } from "@ide/shared";
import type { Notification } from "@/lib/types";
import { useAlertPrefs } from "@/chat/alerts";
import { navigateTo } from "@/desktop/bridge";
import { del, errorMessage, post } from "@/lib/api";
import { osNotify } from "@/lib/os-notify";
import { keys, useNotifications } from "@/lib/queries";
import { cn, GROUP_ROLE_LABEL, timeAgo } from "@/lib/utils";
import { Switch } from "./ui/controls";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/overlay";

type By = { handle?: string; name?: string };
const who = (d: Record<string, unknown>) => `@${(d.by as By | undefined)?.handle ?? "alguém"}`;
const roleName = (role: unknown) =>
  ROLE_LABELS[role as Role] ?? GROUP_ROLE_LABEL[String(role)] ?? String(role ?? "");

const NOTIFICATION_TEXT: Record<string, (d: Record<string, unknown>) => string> = {
  invite_accepted: (d) => `${who(d)} aceitou seu convite${d.name ? ` para ${String(d.name)}` : ""}`,
  invite_declined: (d) => `${who(d)} recusou o convite${d.name ? ` para ${String(d.name)}` : ""}`,
  direct_invite: (d) =>
    `${who(d)} convidou você para ${d.target === "group" ? "o grupo" : "o projeto"} ${String(d.name ?? "")} como ${
      d.role === "owner" ? "Acesso total" : roleName(d.role)
    }`,
  access_request: (d) => `${who(d)} pediu acesso ao projeto ${String(d.projectName ?? "")}`,
  access_granted: (d) =>
    `${who(d)} aceitou seu pedido: você entrou em ${String(d.projectName ?? "um projeto")} como ${roleName(d.role)}`,
  access_declined: (d) => `${who(d)} recusou seu pedido de acesso a ${String(d.projectName ?? "um projeto")}`,
  role_changed: (d) => `Seu papel em ${String(d.projectName ?? "um projeto")} mudou para ${roleName(d.role)}`,
  handover: (d) => `@${String(d.by)} passou o controle para você em ${String(d.projectName ?? "um projeto")}`,
  session_review: () => "Sessão encerrada — avalie quem programou com você",
  review_received: (d) => `@${String(d.by)} avaliou a sessão com você (${String(d.rating)}/5)`,
  new_follower: (d) => `${who(d)} começou a seguir você`,
  session_call: (d) =>
    `${who(d)} chamou você para programar junto em ${String(d.name ?? "um projeto")} · ${String(d.minutes ?? 25)} min · você ${
      d.role === "driver" ? "dirige" : "navega"
    }`,
  help_mentor: (d) => `${who(d)} entrou como mentor para ajudar: “${String(d.message ?? "")}”`,
};

/** Endereço do projeto com o cronômetro da sessão em par (aberto ao aceitar um chamado). */
export const pairPath = (d: Record<string, unknown>) =>
  `/p/${String(d.projectId)}?par=${Number(d.minutes) || 25}&papel=${d.role === "driver" ? "driver" : "navigator"}`;

/** Convite pelo nome: aceitar ou recusar direto na notificação. */
export function InviteActions({
  inviteId,
  onDone,
  openPath,
}: {
  inviteId: string;
  onDone: () => void;
  /** Para onde ir ao aceitar (padrão: o projeto ou o grupo). */
  openPath?: string;
}) {
  const respond = useMutation({
    mutationFn: (accept: boolean) =>
      post<{ accepted: boolean; kind: "project" | "group"; projectId?: string; groupSlug?: string | null }>(
        `/me/invitations/${inviteId}`,
        { accept },
      ),
    onSuccess: (res) => {
      onDone();
      if (!res.accepted) return void toast("Convite recusado.");
      toast.success("Convite aceito");
      if (openPath) navigateTo(openPath);
      else if (res.kind === "project" && res.projectId) navigateTo(`/p/${res.projectId}`);
      else if (res.groupSlug) navigateTo(`/g/${res.groupSlug}`);
    },
    onError: (err) => {
      toast.error(errorMessage(err));
      onDone();
    },
  });
  return (
    <span className="mt-2 flex gap-1.5">
      <button
        type="button"
        disabled={respond.isPending}
        onClick={() => respond.mutate(true)}
        className="flex h-7 items-center gap-1 rounded-md bg-fg px-2.5 text-[12px] font-medium text-fg-inverse transition-colors hover:bg-fg/90 disabled:opacity-60"
      >
        <Check className="h-3.5 w-3.5" /> Aceitar
      </button>
      <button
        type="button"
        disabled={respond.isPending}
        onClick={() => respond.mutate(false)}
        className="flex h-7 items-center rounded-md border border-border-strong px-2.5 text-[12px] font-medium text-fg transition-colors hover:bg-bg-emphasis disabled:opacity-60"
      >
        Recusar
      </button>
    </span>
  );
}

/**
 * Pedido de acesso (quem administra o projeto): aceitar com um papel (editor por padrão) ou
 * recusar, direto na notificação. Quem pediu recebe a resposta.
 */
export function AccessRequestActions({
  projectId,
  userId,
  onDone,
}: {
  projectId: string;
  userId: string;
  onDone: () => void;
}) {
  const [role, setRole] = useState<Role>("editor");
  const respond = useMutation({
    mutationFn: (accept: boolean) =>
      post<{ accepted: boolean }>(`/projects/${projectId}/access-requests/${userId}`, { accept, role }),
    onSuccess: (res) => {
      onDone();
      toast(res.accepted ? `Acesso liberado como ${ROLE_LABELS[role]}.` : "Pedido recusado.");
    },
    onError: (err) => {
      toast.error(errorMessage(err));
      onDone();
    },
  });
  return (
    <span className="mt-2 flex flex-wrap items-center gap-1.5">
      <span className="flex h-7 items-stretch overflow-hidden rounded-md bg-accent text-accent-fg">
        <button
          type="button"
          disabled={respond.isPending}
          onClick={() => respond.mutate(true)}
          className="flex items-center gap-1 px-2.5 text-[12px] font-medium transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          <Check className="h-3.5 w-3.5" /> Aceitar como {ROLE_LABELS[role]}
        </button>
        <Menu>
          <MenuTrigger
            className="flex w-6 items-center justify-center border-l border-black/15 outline-none hover:opacity-90"
            aria-label="Escolher o papel"
          >
            <ChevronDown className="h-3 w-3" />
          </MenuTrigger>
          <MenuContent align="start" className="w-44">
            {INVITABLE_ROLES.map((r) => (
              <MenuItem key={r} onSelect={() => setRole(r)}>
                <span className="flex flex-1 items-center justify-between">
                  {ROLE_LABELS[r]}
                  {r === role ? <Check className="h-3.5 w-3.5" /> : null}
                </span>
              </MenuItem>
            ))}
          </MenuContent>
        </Menu>
      </span>
      <button
        type="button"
        disabled={respond.isPending}
        onClick={() => respond.mutate(false)}
        className="flex h-7 items-center rounded-md border border-border-strong px-2.5 text-[12px] font-medium text-fg transition-colors hover:bg-bg-emphasis disabled:opacity-60"
      >
        Recusar
      </button>
    </span>
  );
}

/** Texto da notificação (o mesmo no sino e no aviso que aparece na hora). */
export function notificationText(n: Pick<Notification, "kind" | "data">): string {
  return NOTIFICATION_TEXT[n.kind]?.(n.data) ?? n.kind;
}

/** Para onde a notificação leva (projeto, grupo, perfil). */
export function targetOf(kind: string, d: Record<string, unknown>): string | null {
  if (
    kind === "access_request" ||
    kind === "access_granted" ||
    kind === "role_changed" ||
    kind === "handover"
  )
    return d.projectId ? `/p/${String(d.projectId)}` : null;
  if (kind === "invite_accepted")
    return d.projectId ? `/p/${String(d.projectId)}` : d.groupSlug ? `/g/${String(d.groupSlug)}` : null;
  if (kind === "new_follower") return (d.by as By | undefined)?.handle ? `/u/${(d.by as By).handle}` : null;
  // Chamado de quem já participa do projeto (sem convite): abre direto com o cronômetro.
  if (kind === "session_call" && d.projectId && !d.inviteId) return pairPath(d);
  if (kind === "help_mentor") return d.projectId ? `/p/${String(d.projectId)}` : null;
  return null;
}

/** Sino de notificações (barra de título do app e cabeçalho da web). */
export function Notifications({
  enabled,
  side = "bottom",
  className,
}: {
  enabled: boolean;
  side?: "bottom" | "right";
  className?: string;
}) {
  const qc = useQueryClient();
  const { data } = useNotifications(enabled);
  const dnd = useAlertPrefs((s) => s.dnd);
  const setPrefs = useAlertPrefs((s) => s.set);
  const markRead = useMutation({
    mutationFn: () => post("/notifications/read", {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.notifications });
      void qc.invalidateQueries({ queryKey: keys.me });
    },
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: keys.notifications });
    void qc.invalidateQueries({ queryKey: keys.me });
    void qc.invalidateQueries({ queryKey: keys.projects });
    void qc.invalidateQueries({ queryKey: keys.groups });
  };
  const removeOne = useMutation({
    mutationFn: (id: string) => del(`/notifications/${id}`),
    onSuccess: refresh,
  });
  const clearAll = useMutation({ mutationFn: () => del("/notifications"), onSuccess: refresh });
  const unread = data?.unread ?? 0;
  const items = data?.items ?? [];
  return (
    <Popover onOpenChange={(open) => open && unread > 0 && markRead.mutate()}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Notificações${dnd ? " (não perturbe)" : ""}${unread ? ` (${unread} novas)` : ""}`}
          className={cn(
            "relative flex h-8 w-8 items-center justify-center rounded-md text-fg-muted outline-none transition-colors hover:bg-bg-muted hover:text-fg focus-visible:ring-2 focus-visible:ring-ring/60 data-[state=open]:bg-bg-emphasis data-[state=open]:text-fg",
            className,
          )}
        >
          {dnd ? (
            <BellOff className="h-[17px] w-[17px]" strokeWidth={1.75} />
          ) : (
            <Bell className="h-[17px] w-[17px]" strokeWidth={1.75} />
          )}
          {unread > 0 ? (
            <span className="absolute right-[5px] top-[4px] flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-accent px-1 text-[9.5px] font-bold text-accent-fg ring-2 ring-canvas">
              {unread > 9 ? "9+" : unread}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" side={side} className="w-[360px] p-0">
        <div className="flex items-center justify-between px-4 pb-2 pt-3">
          <p className="text-[13px] font-semibold">Notificações</p>
          <span className="flex items-center gap-2">
            {unread > 0 ? <span className="text-[11.5px] text-fg-subtle">{unread} novas</span> : null}
            {items.length ? (
              <button
                type="button"
                onClick={() => clearAll.mutate()}
                className="flex h-6 items-center gap-1 rounded-md px-1.5 text-[11.5px] text-fg-subtle transition-colors hover:bg-bg-muted hover:text-fg"
              >
                <Trash2 className="h-3 w-3" /> Limpar tudo
              </button>
            ) : null}
          </span>
        </div>
        {/* Silenciar de verdade: nenhum som, notificação do sistema ou balão de mensagem. */}
        <label className="mx-1 mb-1 flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2 hover:bg-bg-muted">
          <BellOff className="h-3.5 w-3.5 shrink-0 text-fg-muted" />
          <span className="min-w-0 flex-1">
            <span className="block text-[12.5px] text-fg">Não perturbe</span>
            <span className="block text-[11px] text-fg-subtle">
              {dnd ? "Sem som, notificação ou balão até desligar" : "Silencia todos os avisos de mensagem"}
            </span>
          </span>
          <Switch checked={dnd} onCheckedChange={(v) => setPrefs({ dnd: v })} aria-label="Não perturbe" />
        </label>
        <ul className="max-h-96 overflow-y-auto border-t border-border pb-1 pt-1">
          {items.length === 0 ? (
            <li className="px-4 pb-8 pt-6 text-center text-[13px] text-fg-subtle">Você está em dia.</li>
          ) : (
            items.map((n) => {
              const href = targetOf(n.kind, n.data);
              return (
                <li key={n.id} className="group mx-1 flex gap-2.5 rounded-md px-3 py-2.5 hover:bg-bg-muted">
                  <span
                    className={cn(
                      "mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full",
                      n.readAt ? "bg-transparent" : "bg-accent",
                    )}
                  />
                  <span className="min-w-0">
                    {href ? (
                      <button
                        type="button"
                        onClick={() => navigateTo(href)}
                        className="block text-left text-[13px] leading-snug text-fg hover:underline"
                      >
                        {NOTIFICATION_TEXT[n.kind]?.(n.data) ?? n.kind}
                      </button>
                    ) : (
                      <span className="block text-[13px] leading-snug text-fg">
                        {NOTIFICATION_TEXT[n.kind]?.(n.data) ?? n.kind}
                      </span>
                    )}
                    <span className="mt-0.5 block text-[11.5px] text-fg-subtle">{timeAgo(n.createdAt)}</span>
                    {n.kind === "direct_invite" && typeof n.data.inviteId === "string" ? (
                      <InviteActions inviteId={n.data.inviteId} onDone={refresh} />
                    ) : null}
                    {n.kind === "session_call" && typeof n.data.inviteId === "string" ? (
                      <InviteActions
                        inviteId={n.data.inviteId}
                        onDone={refresh}
                        openPath={pairPath(n.data)}
                      />
                    ) : null}
                    {n.kind === "access_request" &&
                    typeof n.data.projectId === "string" &&
                    typeof (n.data.by as (By & { id?: string }) | undefined)?.id === "string" ? (
                      <AccessRequestActions
                        projectId={n.data.projectId}
                        userId={(n.data.by as { id: string }).id}
                        onDone={refresh}
                      />
                    ) : null}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeOne.mutate(n.id)}
                    className="ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-fg-subtle opacity-0 transition-opacity hover:bg-bg-emphasis hover:text-fg focus-visible:opacity-100 group-hover:opacity-100"
                    aria-label="Apagar notificação"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

/** Notificações que também avisam pelo sistema quando o Kompil está em segundo plano. */
/** Convites e chamados também viram notificação do Windows (com o app em foco ou não). */
const SYSTEM_WORTHY = new Set([
  "session_call",
  "direct_invite",
  "help_mentor",
  "access_request",
  "access_granted",
  "invite_accepted",
  "role_changed",
]);

function systemNotify(id: string, text: string, href: string | null) {
  osNotify({
    id: `notification-${id}`,
    title: "Kompil",
    body: text,
    onClick: () => {
      if (href) navigateTo(href);
    },
  });
}

/** Tipos que pedem uma resposta (o aviso fica mais tempo e tem botão). */
const ACTIONABLE = new Set(["direct_invite", "access_request", "session_call"]);

/**
 * Avisa na hora (balão no canto) quando chega notificação nova — em qualquer tela, inclusive com
 * um projeto aberto, onde o sino fica pequeno no canto. Busca a cada 20 s e quando a janela volta
 * ao foco; respeita o "Não perturbe". Notificações que já existiam ao abrir não viram balão.
 */
export function useNotificationToasts(enabled: boolean) {
  const qc = useQueryClient();
  const { data } = useNotifications(enabled, 20_000);
  const dnd = useAlertPrefs((s) => s.dnd);
  const seen = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!data) return;
    if (seen.current === null) {
      seen.current = new Set(data.items.map((n) => n.id));
      return;
    }
    const fresh = data.items.filter((n) => !n.readAt && !seen.current!.has(n.id));
    for (const n of fresh) seen.current.add(n.id);
    if (dnd) return;
    for (const n of fresh.slice(0, 3)) {
      const href = targetOf(n.kind, n.data);
      // Chamados e convites também vão para as notificações do sistema, como as mensagens.
      if (SYSTEM_WORTHY.has(n.kind)) systemNotify(n.id, notificationText(n), href);
      toast(notificationText(n), {
        id: `notification-${n.id}`,
        duration: ACTIONABLE.has(n.kind) ? 15_000 : 6_000,
        description: ACTIONABLE.has(n.kind) ? "Responda pelo sino de notificações." : undefined,
        action: href
          ? { label: n.kind === "access_request" ? "Ver" : "Abrir", onClick: () => navigateTo(href) }
          : undefined,
      });
    }
    if (fresh.some((n) => n.kind === "access_granted" || n.kind === "invite_accepted"))
      void qc.invalidateQueries({ queryKey: keys.projects });
  }, [data, dnd, qc]);
}
